import { ReactNode } from 'react';
import { IN_ARTICLE_AD_PROVIDERS } from './zone';
import { InContentAd } from './inContentAd';
import { Upsell } from '../upsells/index';

/** Number of rows between each interstitial slot */
export const LEADERBOARD_AD_INTERVAL = 20;

/** Maximum number of interstitial slots per page */
export const MAX_LEADERBOARD_INTERSTITIALS = 4;

/** Default pattern: 75% ads, 25% upsells */
export const DEFAULT_LEADERBOARD_AD_PATTERN: ('ad' | 'upsell')[] = ['ad', 'ad', 'upsell', 'ad'];

/**
 * Determines whether a given slot should show an ad or an upsell.
 * Uses stable randomization via seed so the pattern is consistent per page load.
 */
export function getLeaderboardSlotType(
  slotIndex: number,
  seed: number,
  pattern: ('ad' | 'upsell')[] = DEFAULT_LEADERBOARD_AD_PATTERN
): 'ad' | 'upsell' {
  return pattern[(seed + slotIndex) % pattern.length];
}

/**
 * Returns the interstitial slot index (0-based) if an interstitial should be shown
 * before the row at `rowIndex`, or `false` if not.
 */
export function shouldShowLeaderboardInterstitial(
  rowIndex: number,
  totalRows: number,
  currentFeatures: { no_ads?: boolean },
  siteConfig: { displayAdsProvider?: string }
): number | false {
  if (currentFeatures.no_ads) {
    return false;
  }
  if (!IN_ARTICLE_AD_PROVIDERS.includes(siteConfig.displayAdsProvider || '')) {
    return false;
  }
  if (rowIndex === 0 || rowIndex % LEADERBOARD_AD_INTERVAL !== 0) {
    return false;
  }
  const slotIndex = Math.floor(rowIndex / LEADERBOARD_AD_INTERVAL) - 1;
  if (slotIndex >= MAX_LEADERBOARD_INTERSTITIALS) {
    return false;
  }
  if (totalRows <= rowIndex) {
    return false;
  }
  return slotIndex;
}

/**
 * Renders the content for a leaderboard interstitial slot (ad or upsell).
 */
export function LeaderboardInterstitialContent({ slotIndex, seed }: { slotIndex: number; seed: number }): ReactNode {
  const slotType = getLeaderboardSlotType(slotIndex, seed);
  if (slotType === 'upsell') {
    return <Upsell seedOffset={slotIndex} fallback={<InContentAd />} />;
  }
  return <InContentAd />;
}

/**
 * Renders a `<tr>` containing a leaderboard interstitial (ad or upsell).
 * Use in `<table>`-based ranking components.
 */
export function LeaderboardInterstitialRow({
  slotIndex,
  seed,
  colSpan
}: {
  slotIndex: number;
  seed: number;
  colSpan: number;
}) {
  return (
    <tr>
      <td colSpan={colSpan} className="slds-text-align--center slds-p-around--x-small rio-simple-table-cell">
        <div className="rio-simple-table-cell__inner">
          <LeaderboardInterstitialContent slotIndex={slotIndex} seed={seed} />
        </div>
      </td>
    </tr>
  );
}
